(() => {
  'use strict';

  const GLOBAL_KEY = '__WIWelcomePopupV4__';
  const ROOT_SELECTOR = '[data-wi-welcome-popup]';

  /*
    Shopify can execute section assets again while the theme editor reloads a
    section. Reuse one controller instead of binding duplicate global events.
  */
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  function readStorage(key) {
    try {
      const value = window.localStorage.getItem(key);
      return value ? JSON.parse(value) : {};
    } catch (error) {
      return {};
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // The popup still functions when storage is unavailable.
    }
  }

  function daysToMilliseconds(days) {
    return Math.max(0, Number(days) || 0) * 24 * 60 * 60 * 1000;
  }

  function isRecent(timestamp, days) {
    const savedTime = Number(timestamp);
    if (!Number.isFinite(savedTime) || savedTime <= 0) return false;

    return Date.now() - savedTime < daysToMilliseconds(days);
  }

  function initPopup(root) {
    if (!root || instances.has(root)) return;

    const dialog = root.querySelector('[data-wi-welcome-dialog]');
    if (!dialog) return;

    const searchParams = new URLSearchParams(window.location.search);
    const previewMode =
      searchParams.has('preview_theme_id') ||
      searchParams.get('_fd') === '0' ||
      window.Shopify?.theme?.role === 'unpublished' ||
      window.Shopify?.theme?.role === 'development';

    const settings = {
      enabled: root.dataset.enabled === 'true',
      pageEligible: root.dataset.pageEligible === 'true',
      designMode: root.dataset.designMode === 'true',
      previewMode,
      testMode: root.dataset.testMode === 'true',
      delay: Math.max(0, Number(root.dataset.delayMs) || 0),
      dismissDays: Math.max(1, Number(root.dataset.dismissDays) || 30),
      successDays: Math.max(30, Number(root.dataset.successDays) || 3630),
      version: (root.dataset.storageVersion || 'v1').trim() || 'v1'
    };

    const storageKey = [
      'wiWelcomePopup',
      window.location.hostname,
      settings.version
    ].join(':');

    const closeTriggers = Array.from(
      dialog.querySelectorAll('[data-wi-welcome-close]')
    );
    const closeButton = dialog.querySelector('.wi-welcome-popup__close');
    const copyButton = dialog.querySelector('[data-wi-welcome-copy]');
    const codeElement = dialog.querySelector('[data-wi-welcome-code]');
    const copyStatus = dialog.querySelector(
      '[data-wi-welcome-copy-status]'
    );
    const cta = dialog.querySelector('[data-wi-welcome-cta]');
    const form = dialog.querySelector('form');
    const signupSuccess = Boolean(
      dialog.querySelector('[data-wi-signup-success]')
    );
    const video = dialog.querySelector('[data-wi-welcome-video]');

    const abortController = new AbortController();
    const { signal } = abortController;

    const state = {
      record: readStorage(storageKey),
      openTimer: null,
      preloadTimer: null,
      closeTimer: null,
      lastFocusedElement: null,
      isOpen: false,
      isClosing: false,
      portaled: false,
      destroyed: false
    };

    function clearTimer(name) {
      if (state[name] === null) return;

      window.clearTimeout(state[name]);
      state[name] = null;
    }

    function clearAllTimers() {
      clearTimer('openTimer');
      clearTimer('preloadTimer');
      clearTimer('closeTimer');
    }

    function prepareVideo() {
      if (!video || reduceMotionQuery.matches) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      if (
        video.readyState === HTMLMediaElement.HAVE_NOTHING ||
        video.networkState === HTMLMediaElement.NETWORK_EMPTY
      ) {
        try {
          video.load();
        } catch (error) {
          // The poster remains visible if media loading is blocked.
        }
      }
    }

    function playVideo() {
      if (!video || reduceMotionQuery.matches || !state.isOpen) return;

      prepareVideo();
      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    function stopVideo(reset = true) {
      if (!video) return;

      try {
        video.pause();
        if (reset) video.currentTime = 0;
      } catch (error) {
        // The video may not have loaded metadata yet.
      }
    }

    function lockPage() {
      document.documentElement.classList.add('wi-welcome-popup-open');
      document.body.classList.add('wi-welcome-popup-open');
    }

    function unlockPage() {
      document.documentElement.classList.remove('wi-welcome-popup-open');
      document.body.classList.remove('wi-welcome-popup-open');
    }

    function saveDismissal() {
      if (
        settings.testMode ||
        settings.designMode ||
        settings.previewMode
      ) return;

      state.record.dismissedAt = Date.now();
      writeStorage(storageKey, state.record);
    }

    function saveSignupSuccess() {
      if (
        settings.testMode ||
        settings.designMode ||
        settings.previewMode
      ) return;

      state.record.subscribedAt = Date.now();
      delete state.record.dismissedAt;
      writeStorage(storageKey, state.record);
    }

    function isEligible() {
      /* Disabled or excluded pages must never open. */
      if (!settings.enabled) {
        root.dataset.wiPopupStatus = 'disabled';
        return false;
      }

      if (!settings.pageEligible) {
        root.dataset.wiPopupStatus = 'page-ineligible';
        return false;
      }

      /* Always show the post-submit confirmation. */
      if (signupSuccess) {
        root.dataset.wiPopupStatus = 'signup-success';
        return true;
      }

      /*
        Test mode, the theme editor, and Shopify's unpublished-theme preview
        bypass visitor-history restrictions but still honour the selected delay.
      */
      if (
        settings.testMode ||
        settings.designMode ||
        settings.previewMode
      ) {
        root.dataset.wiPopupStatus = settings.previewMode
          ? 'preview-eligible'
          : 'test-eligible';
        return true;
      }

      if (isRecent(state.record.subscribedAt, settings.successDays)) {
        root.dataset.wiPopupStatus = 'hidden-after-signup';
        return false;
      }

      if (isRecent(state.record.dismissedAt, settings.dismissDays)) {
        root.dataset.wiPopupStatus = 'hidden-after-dismissal';
        return false;
      }

      root.dataset.wiPopupStatus = 'eligible';
      return true;
    }

    function ensureDialogInBody() {
      if (dialog.parentElement === document.body) return;

      document.body.appendChild(dialog);
      state.portaled = true;
    }

    function finishClose() {
      if (state.destroyed) return;

      try {
        if (dialog.open && typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      } catch (error) {
        dialog.removeAttribute('open');
      }

      dialog.dataset.wiOpen = 'false';
      dialog.classList.remove('is-visible');
      state.isOpen = false;
      state.isClosing = false;

      unlockPage();
      stopVideo(true);

      if (state.lastFocusedElement?.isConnected) {
        state.lastFocusedElement.focus?.({ preventScroll: true });
      }

      state.lastFocusedElement = null;
    }

    function closePopup({ remember = true } = {}) {
      if (!state.isOpen || state.isClosing || state.destroyed) return;

      state.isClosing = true;
      clearTimer('openTimer');
      clearTimer('preloadTimer');

      if (remember) saveDismissal();

      dialog.classList.remove('is-visible');

      if (reduceMotionQuery.matches) {
        finishClose();
        return;
      }

      state.closeTimer = window.setTimeout(finishClose, 330);
    }

    function openPopup({ immediateAnimation = false } = {}) {
      if (
        state.isOpen ||
        state.isClosing ||
        state.destroyed ||
        !settings.enabled ||
        !settings.pageEligible
      ) {
        return;
      }

      clearAllTimers();
      ensureDialogInBody();

      state.lastFocusedElement = document.activeElement;
      state.isOpen = true;
      root.dataset.wiPopupStatus = 'open';
      dialog.dataset.wiOpen = 'true';

      try {
        if (typeof dialog.showModal === 'function' && !dialog.open) {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      } catch (error) {
        dialog.setAttribute('open', '');
      }

      lockPage();

      const reveal = () => {
        if (!state.isOpen || state.destroyed) return;

        dialog.classList.add('is-visible');
        playVideo();

        window.setTimeout(() => {
          if (state.isOpen) {
            closeButton?.focus?.({ preventScroll: true });
          }
        }, reduceMotionQuery.matches ? 0 : 220);
      };

      if (immediateAnimation || reduceMotionQuery.matches) {
        reveal();
      } else {
        requestAnimationFrame(() => requestAnimationFrame(reveal));
      }
    }

    function schedulePopup() {
      clearTimer('openTimer');
      clearTimer('preloadTimer');

      if (!isEligible() || state.destroyed) return;

      /*
        The selected delay is always honoured on desktop and mobile, including
        Test Mode and Shopify's theme-editor preview. Only a successful form
        response opens quickly so the customer can see the confirmation.
      */
      const openingDelay = signupSuccess ? 250 : settings.delay;
      root.dataset.wiPopupStatus = `scheduled-${openingDelay}`;

      state.preloadTimer = window.setTimeout(
        prepareVideo,
        Math.max(0, openingDelay - 1500)
      );

      state.openTimer = window.setTimeout(() => {
        state.openTimer = null;
        openPopup({ immediateAnimation: signupSuccess });
      }, openingDelay);
    }

    async function copyDiscountCode() {
      const code = codeElement?.textContent?.trim();
      if (!code || !copyButton) return;

      const defaultLabel =
        copyButton.dataset.copyLabel || copyButton.textContent.trim();
      const successLabel = copyButton.dataset.copiedLabel || 'Copied';

      let copied = false;

      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(code);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }

      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();

        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }

        textarea.remove();
      }

      if (!copied) {
        if (copyStatus) {
          copyStatus.textContent = `Please copy ${code} manually.`;
        }
        return;
      }

      copyButton.textContent = successLabel;
      if (copyStatus) copyStatus.textContent = `${code} copied.`;

      window.setTimeout(() => {
        copyButton.textContent = defaultLabel;
        if (copyStatus) copyStatus.textContent = '';
      }, 2200);
    }

    closeTriggers.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          closePopup({ remember: true });
        },
        { signal }
      );
    });

    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        closePopup({ remember: true });
      },
      { signal }
    );

    copyButton?.addEventListener('click', copyDiscountCode, { signal });

    cta?.addEventListener(
      'click',
      () => {
        saveDismissal();
      },
      { signal }
    );

    form?.addEventListener(
      'submit',
      () => {
        if (
          settings.testMode ||
          settings.designMode ||
          settings.previewMode
        ) return;

        state.record.formSubmittedAt = Date.now();
        writeStorage(storageKey, state.record);
      },
      { signal }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          stopVideo(false);
        } else if (state.isOpen) {
          playVideo();
        }
      },
      { signal }
    );

    if (signupSuccess) saveSignupSuccess();

    const controller = {
      open: openPopup,
      close: closePopup,
      reschedule: schedulePopup,
      destroy() {
        if (state.destroyed) return;

        state.destroyed = true;
        clearAllTimers();
        abortController.abort();

        if (state.isOpen) {
          dialog.classList.remove('is-visible');

          try {
            if (dialog.open && typeof dialog.close === 'function') {
              dialog.close();
            } else {
              dialog.removeAttribute('open');
            }
          } catch (error) {
            dialog.removeAttribute('open');
          }
        }

        state.isOpen = false;
        state.isClosing = false;
        stopVideo(false);
        unlockPage();

        if (state.portaled && dialog.isConnected) {
          dialog.remove();
        }

        instances.delete(root);
      }
    };

    instances.set(root, controller);
    schedulePopup();
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) {
      initPopup(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initPopup);
  }

  function getRoots(scope) {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      roots.push(root);
    });

    return roots;
  }

  const api = {
    initAll,

    open(sectionId) {
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (!sectionId || root.dataset.sectionId === sectionId) {
          instances.get(root)?.open?.({ immediateAnimation: false });
        }
      });
    },

    close(sectionId) {
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (!sectionId || root.dataset.sectionId === sectionId) {
          instances.get(root)?.close?.({ remember: false });
        }
      });
    }
  };

  window[GLOBAL_KEY] = api;
  window.WIWelcomePopup = api;

  initAll(document);

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    getRoots(event.target).forEach((root) => {
      instances.get(root)?.destroy?.();
    });
  });

  /*
    Intentionally do not auto-open on shopify:section:select.
    Shopify can fire that event when switching between desktop and mobile
    previews, which would bypass the configured delay.
  */
})();
